import { canonicalRequestPayload, turnUsernameExpiry, } from '@streamr/tv-engine/~/turncredential';
import { binaryToHex, EcdsaSecp256r1 } from '@streamr/utils';
import { Env } from '../../Env';
const LOG_PREFIX = '[turn-cred]';
const CACHE_KEY_PREFIX = 'streamrtv.turnCredential.';
const RPC_TIMEOUT_MS = 15_000;
const MIN_REMAINING_MS = 5 * 60_000;
const REFRESH_FRACTION = 0.75;
const RETRY_BACKOFF_MS = [60_000, 5 * 60_000, 15 * 60_000];
export class TurnCredentialManager {
    transport;
    privateKey;
    streamId;
    signing = new EcdsaSecp256r1();
    refreshTimer;
    failures = 0;
    warnedOnce = false;
    stopped = false;
    onVisible = () => {
        if (document.visibilityState === 'visible') {
            this.scheduleRefresh(0);
        }
    };
    onOnline = () => this.scheduleRefresh(0);
    constructor(transport, privateKey, streamId) {
        this.transport = transport;
        this.privateKey = privateKey;
        this.streamId = streamId;
    }
    /** The dedicated-TURN configuration, or undefined when not configured. */
    static config() {
        const host = Env.TURN_SERVER_HOST;
        const nodeId = Env.TURN_CREDENTIAL_NODE_ID;
        if (host === undefined || nodeId === undefined) {
            return undefined;
        }
        return {
            host,
            nodeId,
            port: Env.TURN_CREDENTIAL_NODE_PORT,
            tls: Env.TURN_CREDENTIAL_NODE_TLS,
        };
    }
    /** True when the app is configured with a credential node + TURN host. */
    static isConfigured() {
        return TurnCredentialManager.config() !== undefined;
    }
    /**
     * ICE servers to start the network node with: STUN plus the cached
     * credential when one is still usable. Never throws, never blocks on
     * the network — minting requires the node to be running.
     */
    getInitialIceServers() {
        const config = TurnCredentialManager.config();
        if (config === undefined) {
            return TurnCredentialManager.stunOnly();
        }
        const cached = this.readCache();
        if (cached !== undefined &&
            this.remainingMs(cached) > MIN_REMAINING_MS) {
            return TurnCredentialManager.buildIceServers(cached.host, cached);
        }
        return TurnCredentialManager.buildIceServers(config.host, undefined);
    }
    /**
     * Mint (or reuse) a credential over the running node and push it into
     * the network stack, then keep refreshing at 75 % of the TTL. Also
     * refreshes on tab visibility / online events (mobile timer throttling).
     */
    async start() {
        if (!TurnCredentialManager.isConfigured()) {
            this.warnOnce('not configured (TURN_SERVER_HOST / TURN_CREDENTIAL_NODE_ID) — running STUN-only');
            return;
        }
        this.stopped = false;
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.onVisible);
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('online', this.onOnline);
        }
        await this.refresh();
    }
    stop() {
        this.stopped = true;
        if (this.refreshTimer !== undefined) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = undefined;
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.onVisible);
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('online', this.onOnline);
        }
    }
    async refresh() {
        if (this.stopped) {
            return;
        }
        const cached = this.readCache();
        const cachedFresh = cached !== undefined &&
            this.remainingMs(cached) > MIN_REMAINING_MS &&
            Date.now() - cached.issuedAt < cached.ttl * 1000 * REFRESH_FRACTION;
        let cred = cachedFresh
            ? cached
            : undefined;
        if (cred === undefined) {
            cred = await this.mint();
        }
        if (cred !== undefined) {
            await this.apply(cred);
            this.failures = 0;
            // refresh at 75 % of the TTL, but never later than 5 min before
            // expiry, and never sooner than 60 s from now
            const remaining = this.remainingMs(cred);
            const issuedAgo = Date.now() - cred.issuedAt;
            const at75 = cred.ttl * 1000 * REFRESH_FRACTION - issuedAgo;
            const beforeExpiry = remaining - MIN_REMAINING_MS;
            const untilRefresh = Math.max(60_000, Math.min(at75, beforeExpiry));
            this.scheduleRefresh(untilRefresh);
            return;
        }
        // mint failed: fall back to an unexpired cache, else STUN-only
        if (cached !== undefined && this.remainingMs(cached) > 0) {
            await this.apply(cached);
            this.warnOnce('credential node unreachable — using cached credential');
        }
        else {
            this.warnOnce('credential node unreachable and no cached credential — running STUN-only');
        }
        const backoff = RETRY_BACKOFF_MS[Math.min(this.failures, RETRY_BACKOFF_MS.length - 1)];
        this.failures++;
        this.scheduleRefresh(backoff);
    }
    scheduleRefresh(delayMs) {
        if (this.stopped) {
            return;
        }
        if (this.refreshTimer !== undefined) {
            clearTimeout(this.refreshTimer);
        }
        this.refreshTimer = setTimeout(() => {
            this.refreshTimer = undefined;
            this.refresh().catch((e) => {
                console.warn(`${LOG_PREFIX} refresh failed`, e);
            });
        }, delayMs);
    }
    async mint() {
        const config = TurnCredentialManager.config();
        if (config === undefined) {
            return undefined;
        }
        const { host } = config;
        const target = config;
        const userId = binaryToHex(this.signing.getPublicKeyFromPrivateKey(this.privateKey), true);
        const fields = {
            userId,
            timestamp: Date.now(),
            nonce: crypto.randomUUID(),
            streamId: this.streamId,
        };
        try {
            const signature = await this.signing.createSignature(canonicalRequestPayload(fields), this.privateKey);
            const res = await this.transport.getTurnCredentials({ ...fields, signature }, target, RPC_TIMEOUT_MS);
            if (res.error !== '' || res.username === '') {
                console.warn(`${LOG_PREFIX} credential node refused request`, {
                    error: res.error,
                });
                return undefined;
            }
            const cred = {
                username: res.username,
                password: res.password,
                ttl: res.ttl,
                expiresAt: res.expiresAt,
                host,
                issuedAt: Date.now(),
            };
            this.writeCache(cred);
            console.log(`${LOG_PREFIX} credential minted`, {
                expiresAt: res.expiresAt,
                ttl: res.ttl,
            });
            return cred;
        }
        catch (e) {
            console.warn(`${LOG_PREFIX} mint failed`, e);
            return undefined;
        }
    }
    async apply(cred) {
        await this.transport.setIceServers(TurnCredentialManager.buildIceServers(cred.host, cred));
    }
    remainingMs(cred) {
        const expiry = turnUsernameExpiry(cred.username) ?? cred.expiresAt;
        return expiry * 1000 - Date.now();
    }
    warnOnce(msg) {
        if (!this.warnedOnce) {
            this.warnedOnce = true;
            console.warn(`${LOG_PREFIX} ${msg}`);
        }
    }
    cacheKey() {
        return (CACHE_KEY_PREFIX +
            binaryToHex(this.signing.getPublicKeyFromPrivateKey(this.privateKey), true));
    }
    readCache() {
        try {
            const raw = globalThis.localStorage?.getItem(this.cacheKey());
            if (!raw) {
                return undefined;
            }
            const parsed = JSON.parse(raw);
            if (typeof parsed.username !== 'string' ||
                typeof parsed.password !== 'string' ||
                typeof parsed.host !== 'string' ||
                parsed.host !== Env.TURN_SERVER_HOST) {
                return undefined;
            }
            return parsed;
        }
        catch {
            return undefined;
        }
    }
    writeCache(cred) {
        try {
            globalThis.localStorage?.setItem(this.cacheKey(), JSON.stringify(cred));
        }
        catch {
            // storage unavailable — fine, we just won't have a fallback
        }
    }
    static stunOnly() {
        const host = Env.TURN_SERVER_HOST;
        return host !== undefined
            ? [{ url: `stun:${host}`, port: 3478 }]
            : [{ url: 'stun:stun.streamr.network', port: 5349 }];
    }
    /** STUN + TURN (udp) + TURNS (tls/tcp) on the dedicated host. */
    static buildIceServers(host, cred) {
        const servers = [{ url: `stun:${host}`, port: 3478 }];
        if (cred !== undefined) {
            servers.push({
                url: `turn:${host}`,
                port: 3478,
                username: cred.username,
                password: cred.password,
            }, {
                url: `turns:${host}`,
                port: 5349,
                username: cred.username,
                password: cred.password,
                tcp: true,
            });
        }
        return servers;
    }
}
